import React, { useEffect, useMemo } from 'react';
import { Box, CircularProgress } from '@mui/material';
import type { PageEntityConfig } from 'c-config';
import { PostAuthRoutes } from 'c-routes';

import { campaignIcon } from 'c-main/icons';

import type { Campaign } from 'c-sdk';
import { PermissionName } from 'c-sdk';
import { campaignName } from 'c-main/Lib/Campaign';
import { useAuthenticatedUser, useEntityPage } from 'c-hooks';
import { Alert, LinkButton } from 'c-components';
import { Translate } from 'c-translation';

import { useEntityData } from 'c-data';
import { useHistory } from 'react-router-dom';
import ReactGA from 'react-ga';
import {
    costPerms,
    creativesPerms,
    displaysPerms,
    reportsPerms,
} from 'c-main/Pages/Campaign/CampaignOverviewPage/SideBar/constants';
import { useUserPermissions } from 'c-auth-module/Hooks';
import { campaignLandingRoute } from './campaignLandingRoute';

const CampaignOverviewPage = () => {
    const { id } = useEntityPage();
    const { hasAll } = useUserPermissions();
    const { getById } = useEntityData<Campaign>('Campaign');
    const campaign = getById({ id });
    const history = useHistory();
    useEffect(() => {
        ReactGA.pageview(window.location.pathname + window.location.search);
    }, []);
    const user = useAuthenticatedUser();
    const reporting = hasAll(reportsPerms);
    const displays = hasAll(displaysPerms);
    const creatives = hasAll(creativesPerms);
    const costs = hasAll(costPerms);
    const email = user?.user?.email;
    const landingRoute = useMemo(
        () =>
            campaign
                ? campaignLandingRoute(campaign, { reporting, displays, creatives, costs }, email)
                : null,
        [campaign, reporting, displays, creatives, costs, email],
    );
    useEffect(() => {
        if (landingRoute) history.replace(landingRoute);
    }, [landingRoute, history]);

    if (campaign && !landingRoute) {
        return (
            <Box p={2}>
                <Alert
                    severity="info"
                    action={
                        <LinkButton size="small" to={PostAuthRoutes.Campaigns.CampaignList}>
                            <Translate path="Modules.Main.Campaigns.Overview.backToList" />
                        </LinkButton>
                    }
                >
                    <Translate path="Modules.Main.Campaigns.Overview.noTabAccess" />
                </Alert>
            </Box>
        );
    }

    return <CircularProgress />;
};
const setup: PageEntityConfig<Campaign> = {
    id: 'CampaignOverviewPage',
    component: CampaignOverviewPage,
    systemSearch: {
        icon: campaignIcon,
        saveAsRecentPage: true,
        title: undefined,
        description: 'Modules.Main.Campaigns.Overview.description',
        type: 'entity',
        entityName: 'Campaign',
        defaultIncludes: [
            'agency',
            'market',
            'creatives',
            'client',
            'creatives.resolution',
            'creatives.file',
            'budgetSummary',
            'budgetSummary.lineItems',
            'budgetSummaries',
            'budgetSummaries.lineItems',
            'notices',
            'errors',
            'creativeThumbnails',
            'bookingSummaries',
            'popShotsCount',
            'missingDisplaysCount',
            'happydemics',
            'additionalContacts',
            // 'budgetSummaries.lineItems.lineItem',
        ],
        prefix: 'SystemSearch.prefixes.campaign',
        prefixDescription: 'SystemSearch.prefixDescriptions.campaign',
        nameGen: (entity, id) => (entity ? campaignName(entity) : String(id)),
        searchColumn: ['name', 'reference'],
        alwaysSearchForEntity: true,
        // aliases: [],
        route: PostAuthRoutes.Campaigns.CampaignOverview,
        permissions: [PermissionName.UniledportalCampaignRead],
        breadcrumbs: [
            {
                label: 'Modules.Main.Campaigns.List.metaTitle',
                link: PostAuthRoutes.Campaigns.CampaignList,
            },
        ],
    },
};

export default setup;
